import React, { useMemo } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
} from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import ValidationError from '../../components/ValidationError';
import { useTrans } from '../../hooks/useTrans';

function FormRow({ label, required = false, children }) {
    return (
        <div className="flex items-center gap-4">
            <label className="w-48 text-sm font-bold text-[#003545] shrink-0">
                {label}{required && ' *'}
            </label>
            <div className="w-full max-w-md">
                {children}
            </div>
        </div>
    );
}

function CheckboxRow({ children }) {
    return (
        <div className="flex items-center gap-4">
            <div className="w-48 shrink-0"></div>
            <div className="w-full max-w-md">
                {children}
            </div>
        </div>
    );
}

export function AddressForm({ debtors, countries, address }) {
    const translate = useTrans();
    const isEditing = !!address?.id;
    const debtorOptions = useMemo(() => debtors.map((debtor) => ({
        value: debtor.id,
        label: `${debtor.relation_number} - ${debtor.company_name}`,
    })), [debtors]);

    const countryOptions = useMemo(() => Object.values(countries), [countries]);

    const { data, setData, post, put, processing, errors } = useForm({
        debtor: address?.debtorId || '',
        addressId: address?.id || '',
        pickup: address?.pickup ? true : false,
        delivery: address?.delivery ? true : false,
        company_name: address?.companyName || '',
        postalcode: address?.postalCode || '',
        housenumber: address?.houseNumber || '',
        housenumberadditional: address?.houseNumberAddition || '',
        street: address?.street || '',
        place: address?.place || '',
        country: address?.country || 'Nederland',
        contact_name: address?.contact || '',
        contact_department: address?.department || '',
        contact_phonenumber: address?.phoneNumber || '',
        contact_email: address?.email || '',
        pickup_note: address?.remarksPickup || '',
        delivery_note: address?.remarksDelivery || '',
    });

    function handleSubmit(e) {
        e.preventDefault();
        if (isEditing) {
            put('/addresses/update');
        } else {
            post('/addresses/toevoegen');
        }
    }

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">
                        {isEditing ? translate('Adres aanpassen') : translate('Adres toevoegen')}
                    </h3>
                </div>

                <div className="px-[30px] pb-[30px]">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <FormRow label={translate('Debiteur')} required>
                            <Combobox
                                items={debtorOptions}
                                value={debtorOptions.find((o) => String(o.value) === String(data.debtor)) ?? null}
                                onValueChange={(item) => setData('debtor', item?.value ?? '')}
                                itemToStringValue={(item) => item.label}
                            >
                                <ComboboxTrigger render={
                                    <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none">
                                        <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                    </Button>
                                } />
                                <ComboboxContent>
                                    <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                    <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                    <ComboboxList>
                                        {(item) => (
                                            <ComboboxItem key={item.value} value={item}>
                                                {item.label}
                                            </ComboboxItem>
                                        )}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                            <ValidationError message={errors.debtor} />
                        </FormRow>

                        <CheckboxRow>
                            <div className="flex items-center gap-2">
                                <Checkbox id="pickup" checked={data.pickup} onCheckedChange={(checked) => setData('pickup', checked)} />
                                <label htmlFor="pickup" className="text-sm text-[#003545] cursor-pointer">{translate('Laadadres')}</label>
                            </div>
                        </CheckboxRow>

                        <CheckboxRow>
                            <div className="flex items-center gap-2">
                                <Checkbox id="delivery" checked={data.delivery} onCheckedChange={(checked) => setData('delivery', checked)} />
                                <label htmlFor="delivery" className="text-sm text-[#003545] cursor-pointer">{translate('Losadres')}</label>
                            </div>
                            <ValidationError message={errors.pickup} />
                        </CheckboxRow>

                        <FormRow label={translate('(Bedrijfs)naam')} required>
                            <Input value={data.company_name} onChange={(e) => setData('company_name', e.target.value)} required className="font-normal" />
                            <ValidationError message={errors.company_name} />
                        </FormRow>

                        <div className="flex items-start gap-4">
                            <label className="w-48 text-sm font-bold text-[#003545] shrink-0 pt-2">
                                {translate('Postcode')} * / {translate('Nr.')} * / {translate('Toev.')}
                            </label>
                            <div className="max-w-md">
                                <div className="flex gap-2">
                                    <Input value={data.postalcode} onChange={(e) => setData('postalcode', e.target.value)} placeholder="0000 AA" required className="w-28 font-normal" />
                                    <Input value={data.housenumber} onChange={(e) => setData('housenumber', e.target.value)} placeholder="0" required className="w-20 font-normal" />
                                    <Input value={data.housenumberadditional} onChange={(e) => setData('housenumberadditional', e.target.value)} placeholder={translate('Toev.')} className="w-20 font-normal" />
                                </div>
                                <ValidationError message={errors.postalcode} />
                                <ValidationError message={errors.housenumber} />
                                <ValidationError message={errors.housenumberadditional} />
                            </div>
                        </div>

                        <FormRow label={translate('Straatnaam')} required>
                            <Input value={data.street} onChange={(e) => setData('street', e.target.value)} required className="font-normal" />
                            <ValidationError message={errors.street} />
                        </FormRow>

                        <FormRow label={translate('Plaatsnaam')} required>
                            <Input value={data.place} onChange={(e) => setData('place', e.target.value)} required className="font-normal" />
                            <ValidationError message={errors.place} />
                        </FormRow>

                        <FormRow label={translate('Land')} required>
                            <Combobox
                                items={countryOptions}
                                value={data.country || null}
                                onValueChange={(value) => setData('country', value ?? '')}
                            >
                                <ComboboxTrigger render={
                                    <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none">
                                        <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                    </Button>
                                } />
                                <ComboboxContent>
                                    <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                    <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                    <ComboboxList>
                                        {(name) => (
                                            <ComboboxItem key={name} value={name}>
                                                {name}
                                            </ComboboxItem>
                                        )}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                            <ValidationError message={errors.country} />
                        </FormRow>

                        <FormRow label={translate('Contactpersoon')}>
                            <Input value={data.contact_name} onChange={(e) => setData('contact_name', e.target.value)} className="font-normal" />
                            <ValidationError message={errors.contact_name} />
                        </FormRow>

                        <FormRow label={translate('Afdeling')}>
                            <Input value={data.contact_department} onChange={(e) => setData('contact_department', e.target.value)} className="font-normal" />
                            <ValidationError message={errors.contact_department} />
                        </FormRow>

                        <FormRow label={translate('Telefoonnummer')}>
                            <Input value={data.contact_phonenumber} onChange={(e) => setData('contact_phonenumber', e.target.value)} className="font-normal" />
                            <ValidationError message={errors.contact_phonenumber} />
                        </FormRow>

                        <FormRow label={translate('E-mailadres')}>
                            <Input type="email" value={data.contact_email} onChange={(e) => setData('contact_email', e.target.value)} className="font-normal" />
                            <ValidationError message={errors.contact_email} />
                        </FormRow>

                        <FormRow label={translate('Laadinstructie')}>
                            <textarea
                                value={data.pickup_note}
                                onChange={(e) => setData('pickup_note', e.target.value)}
                                placeholder={translate('Laadinstructie voor de chauffeur')}
                                className="flex w-full rounded-sm border border-[#bbb] bg-transparent px-3 py-2 text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[80px]"
                            />
                        </FormRow>

                        <FormRow label={translate('Losinstructie')}>
                            <textarea
                                value={data.delivery_note}
                                onChange={(e) => setData('delivery_note', e.target.value)}
                                placeholder={translate('Losinstructie voor de chauffeur')}
                                className="flex w-full rounded-sm border border-[#bbb] bg-transparent px-3 py-2 text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[80px]"
                            />
                        </FormRow>

                        <div>
                            <Button variant="primary" type="submit" disabled={processing}>
                                {translate('Opslaan')}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
